import React from 'react';
import { Modal, Box, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

interface ModalMAIQProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  hideDefaultCloseButton?: boolean;
  customStyle?: React.CSSProperties;
  headerColor?: 'primary' | 'secondary';
}

const ModalMAIQ = ({ 
  open, 
  onClose, 
  children, 
  hideDefaultCloseButton = false,
  customStyle = {},
  headerColor = 'primary'
}: ModalMAIQProps) => {
  const theme = useTheme();

  const getHeaderColor = () => {
    if (headerColor === 'primary') {
      return theme.palette.primary.main;
    }
    return theme.palette.secondary.main;
  };

  const getHeaderContrastText = () => {
    if (headerColor === 'primary') {
      return theme.palette.primary.contrastText || 'white';
    }
    return theme.palette.secondary.contrastText || 'white';
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
    p: 0,
    borderRadius: 0,
    overflow: 'hidden',
    '& .modal-header': {
      backgroundColor: getHeaderColor(),
      color: getHeaderContrastText(),
    },
    '& .modal-header-icon': {
      color: getHeaderContrastText(),
    },
    '& .modal-header-button': {
      color: getHeaderContrastText(),
      '&:hover': {
        backgroundColor: alpha(getHeaderContrastText(), 0.1),
      },
    },
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose} 
      disablePortal
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: alpha(theme.palette.common.black, 0.5),
        },
      }}
    >
      <Box sx={{...modalStyle, ...customStyle}}>
        {!hideDefaultCloseButton && (
          <IconButton 
            onClick={onClose} 
            sx={{ 
              position: 'absolute', 
              right: 8, 
              top: 8, 
              zIndex: 10,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        {children}
      </Box>
    </Modal>
  );
};

export default ModalMAIQ;